import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Wallet, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { formatBRL, todayISO } from '../lib/format';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import CurrencyInput from './CurrencyInput';

export default function WithdrawalDialog({ open, onOpenChange, partnerId, partnerName, currentBalance, onSaved }) {
  const [form, setForm] = useState({ amount: '', date: todayISO(), account_id: '', description: '' });
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ amount: '', date: todayISO(), account_id: '', description: '' });
      api.get('/accounts').then(({ data }) => setAccounts(data));
    }
  }, [open]);

  const newBalance = currentBalance - Number(form.amount || 0);
  const willGoNegative = newBalance < 0;

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Informe o valor do saque');
      return;
    }
    if (!form.account_id) {
      toast.error('Selecione a conta bancária de origem');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/partners/${partnerId}/withdrawals`, {
        amount: Number(form.amount),
        date: form.date,
        account_id: form.account_id,
        description: form.description || null,
      });
      toast.success('Saque registrado');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao registrar saque');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5" /> Registrar saque</DialogTitle>
          <p className="text-xs text-muted-foreground">Sócio: <strong>{partnerName}</strong> · Saldo atual: <strong className="tabular">{formatBRL(currentBalance)}</strong></p>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Valor do saque *</Label>
              <CurrencyInput value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} data-testid="withdrawal-amount-input" />
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>Conta bancária de origem *</Label>
            <Select value={form.account_id} onValueChange={(v) => setForm((f) => ({ ...f, account_id: v }))}>
              <SelectTrigger data-testid="withdrawal-account-input"><SelectValue placeholder="Selecione a conta..." /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}{a.bank ? ` · ${a.bank}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Descrição / Observações</Label>
            <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder='Ex: "Distribuição mensal"' />
          </div>
          {willGoNegative && form.amount && (
            <div className="rounded-lg border border-red-300 bg-[hsl(var(--danger-bg))] p-3 flex items-start gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))] mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-[hsl(var(--danger))]">Saldo ficará negativo</div>
                <div className="text-xs text-[hsl(var(--danger))]">Novo saldo após saque: <strong className="tabular">{formatBRL(newBalance)}</strong> — o sócio ficará com adiantamento.</div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="withdrawal-confirm-button">
            {submitting ? 'Salvando...' : 'Confirmar saque'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
